import { zodResolver } from '@hookform/resolvers/zod';
import {
  Action,
  Card,
  Page,
  TextAreaInput,
  TextInput,
  UrlInput,
  FloatingSave,
} from 'corso-admin-ui';
import { FormEventHandler } from 'react';
import {
  FormProvider,
  useFieldArray,
  useForm,
  useFormContext,
} from 'react-hook-form';
import {
  useConfigSettings,
  useConfigSettingsUpdate,
  useIsShippingTosCheckboxEnabled,
} from '~/hooks/useConfigSettings';
import { shipProtectSettingsFormSchema, UseFormTypes } from '~/types';
import CheckoutWidgetPreview from './CheckoutWidgetPreview';

function useShippingPlusForm() {
  const { data } = useConfigSettings(
    ({ shippingProtection }) => shippingProtection,
  );

  return useForm({
    resolver: zodResolver(shipProtectSettingsFormSchema),
    values: data,
  });
}

function useShippingPlusFormContext() {
  type FormType = UseFormTypes<typeof useShippingPlusForm>;
  return useFormContext<
    FormType['FieldValues'],
    FormType['Context'],
    FormType['TransformedValues']
  >();
}

function ShippingPlusFormFields() {
  const isFreeShippingOptInEnabled = useIsShippingTosCheckboxEnabled();

  const methods = useShippingPlusFormContext();

  return (
    <>
      <TextInput
        required
        id="widget-label"
        details="The primary text of the widget"
        label="Title"
        {...methods.register('widgetConfig.shippingPlus.label')}
        error={
          methods.formState.errors.widgetConfig?.shippingPlus?.label?.message
        }
      />

      {isFreeShippingOptInEnabled && (
        <TextInput
          id="widget-label"
          details="The primary text of the widget when Delivery Guarantee is offered"
          label="Delivery Guarantee Title"
          {...methods.register('widgetConfig.shippingPlus.freeShippingLabel')}
          error={
            methods.formState.errors.widgetConfig?.shippingPlus
              ?.freeShippingLabel?.message
          }
        />
      )}

      <TextAreaInput
        id="widget-description"
        details="The secondary text of the widget"
        label="Description"
        {...methods.register('widgetConfig.shippingPlus.description')}
        error={
          methods.formState.errors.widgetConfig?.shippingPlus?.description
            ?.message
        }
      />
      {isFreeShippingOptInEnabled && (
        <TextAreaInput
          id="widget-description"
          details="The secondary text of the widget when Delivery Guarantee is offered"
          label="Delivery Guarantee Description"
          {...methods.register(
            'widgetConfig.shippingPlus.freeShippingDescription',
          )}
          error={
            methods.formState.errors.widgetConfig?.shippingPlus
              ?.freeShippingDescription?.message
          }
        />
      )}
    </>
  );
}

function ProductExtensionFormFields() {
  const methods = useShippingPlusFormContext();

  return (
    <>
      <TextInput
        required
        id="widget-label"
        details="The primary text of the widget"
        label="Title"
        {...methods.register(
          'widgetConfig.checkoutPlus.productCheckoutExtensionTitle',
        )}
        error={
          methods.formState.errors.widgetConfig?.checkoutPlus
            ?.productCheckoutExtensionTitle?.message
        }
      />

      <TextInput
        required
        id="widget-description"
        details="The secondary text of the widget"
        label="Description"
        {...methods.register(
          'widgetConfig.checkoutPlus.productCheckoutExtensionDescription',
        )}
        error={
          methods.formState.errors.widgetConfig?.checkoutPlus
            ?.productCheckoutExtensionDescription?.message
        }
      />
    </>
  );
}

export default function CheckoutWidgetSettingsPage() {
  const formId = 'shipping-protection-widget';

  const { mutateAsync: saveChanges } = useConfigSettingsUpdate();

  const methods = useShippingPlusForm();

  const { control, handleSubmit, watch } = methods;
  const submitHandler: FormEventHandler = (event) => {
    handleSubmit((values) => saveChanges({ shippingProtection: values }))(
      event,
    ).catch(console.error);
  };

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'widgetConfig.modal.benefits',
  });

  const isVariantProtection =
    watch('widgetConfig.checkoutPlus.checkoutAction') === 'addVariantToCart';

  const isShippingLineProtectionOffered = watch(
    'isShippingLineProtectionOffered',
  );

  const isShippingPlusMode =
    !isVariantProtection && isShippingLineProtectionOffered;

  const modalConfig = watch('widgetConfig.modal');
  const label = isShippingPlusMode
    ? watch('widgetConfig.shippingPlus.label')
    : watch('widgetConfig.checkoutPlus.productCheckoutExtensionTitle');

  const description = isShippingPlusMode
    ? watch('widgetConfig.shippingPlus.description')
    : watch('widgetConfig.checkoutPlus.productCheckoutExtensionDescription');

  const returnTo = new URLSearchParams(window.location.search).get('returnTo');

  return (
    <FormProvider {...methods}>
      <Page
        title="Checkout Widget"
        backAction={
          returnTo ? { to: returnTo, accessibilityLabel: 'Back' } : undefined
        }
      >
        <div className="flex flex-col gap-4">
          <Card>
            <CheckoutWidgetPreview
              label={label ?? ''} // TODO better fallback other than an empty string
              description={description}
              modal={modalConfig}
              showCheckbox={!isShippingPlusMode}
            />
          </Card>
          <form
            className="flex flex-col gap-4"
            onSubmit={submitHandler}
            id={formId}
            onReset={(e) => {
              e.preventDefault();
              methods.reset();
            }}
          >
            <FloatingSave
              isDirty={methods.formState.isDirty}
              isSubmitting={methods.formState.isSubmitting}
              form={formId}
            />

            <Card>
              {isShippingLineProtectionOffered ? (
                <ShippingPlusFormFields />
              ) : (
                <ProductExtensionFormFields />
              )}
            </Card>

            <Card>
              <Card.Heading>Modal</Card.Heading>
              <UrlInput
                required
                id="modal-image-url"
                label="Image URL"
                {...methods.register('widgetConfig.modal.imageUrl')}
                error={
                  methods.formState.errors.widgetConfig?.modal?.imageUrl
                    ?.message
                }
              />

              <TextInput
                id="modal-title"
                label="Title"
                {...methods.register('widgetConfig.modal.title')}
                required
                error={
                  methods.formState.errors.widgetConfig?.modal?.title?.message
                }
              />

              <TextAreaInput
                id="modal-description"
                label="Description"
                {...methods.register('widgetConfig.modal.description')}
                error={
                  methods.formState.errors.widgetConfig?.modal?.description
                    ?.message
                }
              />
            </Card>

            <Card>
              <Card.Line>
                <Card.Heading> Benefits</Card.Heading>
                <Action
                  icon="add"
                  variant="ghost"
                  accessibilityLabel="Add benefit"
                  onClick={() =>
                    append({
                      title: '',
                      description: '',
                    })
                  }
                />
              </Card.Line>
              {fields.map((field, index) => (
                <div key={field.id} className="flex flex-col gap-2">
                  <TextInput
                    id={`benefit-title-${index}`}
                    label="Title"
                    required
                    aria-describedby={`benefit-title-error-${index}`}
                    {...methods.register(
                      `widgetConfig.modal.benefits.${index}.title`,
                    )}
                    error={
                      methods.formState.errors.widgetConfig?.modal?.benefits?.[
                        index
                      ]?.title?.message
                    }
                  />

                  <TextAreaInput
                    id={`benefit-description-${index}`}
                    label="Description"
                    aria-describedby={`benefit-description-error-${index}`}
                    {...methods.register(
                      `widgetConfig.modal.benefits.${index}.description`,
                    )}
                    required
                    error={
                      methods.formState.errors.widgetConfig?.modal?.benefits?.[
                        index
                      ]?.description?.message
                    }
                  />

                  <div className="self-end">
                    <Action onClick={() => remove(index)} variant="destructive">
                      Remove
                    </Action>
                  </div>
                </div>
              ))}
            </Card>
          </form>
        </div>
      </Page>
    </FormProvider>
  );
}
